/**
 * Animation Configuration Constants
 *
 * Centralized animation timings and easing functions for consistent
 * animation behavior across the application.
 */

/**
 * Standard animation durations in milliseconds
 */
export const ANIMATION_DURATIONS = {
  /** Very fast animations (200ms) - micro interactions */
  fast: 200,

  /** Normal animations (400ms) - default for most UI transitions */
  normal: 400,

  /** Slow animations (600ms) - dramatic reveals, section transitions */
  slow: 600,

  /** Delay between animated phrases in text animations */
  phraseDelay: 600,

  /** Typewriter character appearance time */
  typewriterChar: 20,

  /** Pause at phrase boundaries during typewriter */
  typewriterPause: 250,

  /** Section interstitial display time */
  interstitial: 2000,

  /** Gradient transition between sections */
  gradientTransition: 600,

  // ── Header section-label transition ────────────────────────────────────────
  // Marks a section change when no interstitial shows. Tuned so the incoming
  // label settles alongside the `gradientTransition` background cross-fade, so
  // the whole section change reads as one beat.

  /** Pause before the outgoing section label leaves */
  headerLabelSwapDelay: 150,

  /** Outgoing section label fade-out */
  headerLabelOut: 180,

  /** Incoming section label fade-in */
  headerLabelIn: 280,

  /** Section-label emphasis: highlight bloom, then decay back to normal */
  headerLabelEmphasis: 1400,

  // ── New text animation styles ──────────────────────────────────────────────

  /** word-by-word: delay between each word appearing */
  wordByWordDelay: 180,

  /** rise: delay between each phrase sliding up */
  risePhraseDelay: 500,

  /** flash-in: delay between each word's scale-down spring */
  flashInWordDelay: 250,

  /** scramble: how long each letter scrambles before locking to its correct char */
  scrambleLockDelay: 400,

  /** scramble: stagger between letters starting their scramble */
  scrambleLetterStagger: 30,

  /** blur-in: delay between each phrase emerging from blur */
  blurInPhraseDelay: 400,

  /** cascade: delay between each word sliding in from the left */
  cascadeWordDelay: 120,
} as const;

/**
 * Standard easing functions for animations
 * Using Framer Motion's easing names (not CSS easing strings)
 */
export const ANIMATION_EASINGS = {
  /** Linear - constant speed */
  linear: "linear" as const,

  /** Ease out - good for elements appearing */
  easeOut: "easeOut" as const,

  /** Ease in - good for elements disappearing */
  easeIn: "easeIn" as const,

  /** Ease in-out - smooth start and end */
  easeInOut: "easeInOut" as const,
} as const;

/**
 * Framer Motion spring configurations
 */
export const SPRING_CONFIGS = {
  /** Gentle spring - smooth, natural motion */
  gentle: {
    type: "spring" as const,
    stiffness: 300,
    damping: 30,
  },

  /** Bouncy spring - playful, energetic */
  bouncy: {
    type: "spring" as const,
    stiffness: 400,
    damping: 25,
  },

  /** Snappy spring - quick, responsive */
  snappy: {
    type: "spring" as const,
    stiffness: 500,
    damping: 35,
  },

  /** Flash-in spring - word appears oversized then snaps down with slight overshoot */
  flashIn: {
    type: "spring" as const,
    stiffness: 600,
    damping: 20,
    mass: 0.5,
  },
} as const;

/**
 * Standard animation delays for sequencing
 */
export const ANIMATION_DELAYS = {
  /** No delay */
  none: 0,

  /** Short delay between sequential items */
  short: 0.1,

  /** Normal delay between items */
  normal: 0.3,

  /** Long delay for dramatic effect */
  long: 0.5,
} as const;

/**
 * Common Framer Motion transition configurations
 */
export const TRANSITIONS = {
  /** Fast fade transition */
  fastFade: {
    duration: ANIMATION_DURATIONS.fast / 1000,
    ease: ANIMATION_EASINGS.easeOut,
  },

  /** Normal fade transition */
  fade: {
    duration: ANIMATION_DURATIONS.normal / 1000,
    ease: ANIMATION_EASINGS.easeOut,
  },

  /** Slow fade transition */
  slowFade: {
    duration: ANIMATION_DURATIONS.slow / 1000,
    ease: ANIMATION_EASINGS.easeInOut,
  },

  /** Spring transition for buttons */
  button: SPRING_CONFIGS.bouncy,

  /** Spring transition for cards */
  card: SPRING_CONFIGS.gentle,
} as const;

/**
 * Initial animation states for common patterns
 */
export const INITIAL_STATES = {
  /** Hidden - opacity 0 */
  hidden: { opacity: 0 },

  /** Fade from below - slide up with fade */
  fadeUp: { opacity: 0, y: 20 },

  /** Fade from left - slide right with fade */
  fadeLeft: { opacity: 0, x: -8 },

  /** Fade from above - slide down with fade */
  fadeDown: { opacity: 0, y: -20 },

  /** Scale from small */
  scale: { opacity: 0, scale: 0.9 },
} as const;

/**
 * Final animation states
 */
export const ANIMATE_STATES = {
  /** Visible - opacity 1 */
  visible: { opacity: 1 },

  /** Visible with position reset */
  visibleCenter: { opacity: 1, x: 0, y: 0 },

  /** Visible with scale */
  visibleScale: { opacity: 1, scale: 1 },
} as const;
